import moment from 'moment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { GridIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Container, Empty, Spinner, tokens } from '@treams/treams-ui';

import TalentReview from './TalentReview';
import TalentReviewGridPicker from './components/TalentReviewGridPicker';
import TalentReviewPageHeader from './components/TalentReviewPageHeader';
import useTalentReviewPath from './hooks/useTalentReviewPath';
import { oldestActiveTemplateID } from './talentReviewDefaultTemplate';

const Placeholder = styled(Box)`
	position: relative;
	width: 100%;
`;

const GridBackdrop = styled(Box)`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	grid-template-rows: repeat(3, 140px);
	gap: 24px 10px;
	width: 100%;
	opacity: 0.5;
	filter: grayscale(1);
	pointer-events: none;
`;

const GridCell = styled(Box)`
	border-radius: 6px;
	border: 2px solid ${(props) => props.theme.colors.lightBlueDark};
	background: ${tokens.surface.secondary};
`;

const Overlay = styled(Flex)`
	position: absolute;
	inset: 0;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.theme.colors.WhiteTransparantHard};
`;

export default function TalentReviewGroup(props: {
	groupID: string;
	templateID?: string;
	localDate?: graphql.Scalars['LocalDate']['input'];
}) {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation<{ newGrid?: boolean } | undefined>();
	const path = useTalentReviewPath();
	const today = moment().format('YYYY-MM-DD');
	const localDate = props.localDate ?? today;

	// Deliberate "new grid" navigation (no template id in the URL): show the
	// picker instead of redirecting to the latest grid.
	const newGrid = !props.templateID && !!location.state?.newGrid;

	const groupQuery = graphql.useGroupQuery({ variables: { id: props.groupID } });
	const templatesQuery = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
		skip: !!props.templateID || newGrid,
	});

	// A running cycle decides the template and carries its moment into the
	// URL, so the grid shows the cycle callout as if opened from the task.
	const cycleQuery = graphql.useTalentReviewCycleMomentQuery({
		variables: { groupID: props.groupID },
		skip: !!props.templateID || newGrid,
		errorPolicy: 'all',
	});

	const group = groupQuery.data?.group;
	const cycleMoment = cycleQuery.data?.group?.talentReviewCycleMoment;
	const cycleTemplateID = cycleMoment?.momentTemplate.talentReview?.template?.id;

	const defaultTemplateID =
		cycleTemplateID ??
		oldestActiveTemplateID(
			templatesQuery.data?.talentReviewTemplates.edges.map(({ node }) => node) ?? [],
		);
	// A moment_id already in the URL (task link) wins over the resolved one.
	const hasMomentID = new URLSearchParams(location.search).has('moment_id');
	const cycleMomentID = hasMomentID ? undefined : cycleMoment?.id;

	useEffect(() => {
		if (!props.templateID || props.localDate) {
			return;
		}

		history.replace(
			path({
				groupID: props.groupID,
				templateID: props.templateID,
				localDate,
				search: location.search,
			}),
		);
	}, [history, localDate, location.search, path, props.groupID, props.localDate, props.templateID]);

	useEffect(() => {
		if (
			props.templateID ||
			newGrid ||
			templatesQuery.loading ||
			cycleQuery.loading ||
			!defaultTemplateID
		) {
			return;
		}

		history.replace(
			path({
				groupID: props.groupID,
				templateID: defaultTemplateID,
				localDate: today,
				momentID: cycleMomentID,
				search: location.search,
			}),
		);
	}, [
		cycleMomentID,
		cycleQuery.loading,
		defaultTemplateID,
		history,
		location.search,
		newGrid,
		path,
		props.groupID,
		props.templateID,
		templatesQuery.loading,
		today,
	]);

	const handleTemplateChange = (templateID: string) => {
		history.push(
			path({
				groupID: props.groupID,
				templateID,
				localDate,
				search: location.search,
			}),
		);
	};

	if (
		!group ||
		(!props.templateID &&
			!newGrid &&
			(templatesQuery.loading || cycleQuery.loading || defaultTemplateID))
	) {
		return (
			<Flex my={5} justifyContent="center">
				<Spinner />
			</Flex>
		);
	}

	if (props.templateID) {
		return (
			<TalentReview
				embedded
				templateID={props.templateID}
				groupID={props.groupID}
				localDate={localDate}
			/>
		);
	}

	return (
		<>
			<Container pt={4}>
				<TalentReviewPageHeader backLink={path()} title={group.name} />
			</Container>

			<Container py={4}>
				{newGrid ? (
					<Placeholder>
						<GridBackdrop>
							{Array.from({ length: 9 }).map((_, index) => (
								<GridCell key={index} />
							))}
						</GridBackdrop>
						<Overlay>
							<TalentReviewGridPicker
								groupID={props.groupID}
								groupName={group.name}
								onSelect={handleTemplateChange}
							/>
						</Overlay>
					</Placeholder>
				) : (
					// Nothing to fall back on: the workspace has no talent review at all,
					// and adding one is not a manager's job.
					<Box mt={5}>
						<Empty icon={<GridIcon />} title={t('talentReviews.v2.group.noGrid')} small />
					</Box>
				)}
			</Container>
		</>
	);
}
